import { useCallback, useMemo, useState } from 'react';

import { AnnouncementBar } from '@/components/AnnouncementBar';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Divider } from '@/components/Divider';
import { ItineraryCheckoutHeader } from '@/components/ItineraryCheckoutHeader/ItineraryCheckoutHeader';
import { Navbar } from '@/components/Navbar';
import { OverviewCard } from '@/components/OverviewCard';
import { Paragraph } from '@/components/Paragraph';
import { Spacer } from '@/components/Spacer';
import { Tag } from '@/components/Tag';
import { useItinerary } from '@/handlers/useItinerary';
import { usePayment } from '@/hooks/usePayment/usePayment';
import { StyledFlexColumn, StyledFlexRow } from '@/layout/FlexLayout';
import { EnforcePageWidth } from '@/layout/PageLayout';
import { formatPrice } from '@/utils/formatPrice/formatPrice';

import {
  StyledDesktopView,
  StyledLink,
  StyledLogo,
  StyledMobileView,
  StyledOption,
  StyledOptionContent,
  StyledOptionHeader,
  StyledPaymentStatus,
  StyledTable,
  StyledTableCell,
  StyledTableContainer,
  StyledTableHeader,
  StyledTableRow,
} from './styles';

export function PaymentPage() {
  const { data } = useItinerary();
  const { createPayment } = usePayment();

  const [isChecked, setIsChecked] = useState(false);
  const [opened, setOpened] = useState<string | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const [selectedPaymentOptionId, setSelectedPaymentOptionId] = useState<string | null>(null);

  const handleCheckboxChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked);
  }, []);

  const toggleOpened = useCallback((key: string) => {
    setSelectedPaymentMethod(key);
    setOpened((prev) => (prev === key ? prev : key));
  }, []);

  const handlePayment = useCallback(() => {
    if (!selectedPaymentMethod || !selectedPaymentOptionId) return;

    createPayment(selectedPaymentMethod, selectedPaymentOptionId);
  }, [createPayment, selectedPaymentMethod, selectedPaymentOptionId]);

  const payments = useMemo(() => data?.customerPayments || [], [data?.customerPayments]);

  const formattedPrice = useMemo(() => {
    const selectedPayment = payments.find((payment) => payment.id === selectedPaymentOptionId);
    if (!selectedPayment) return false;
    return formatPrice(selectedPayment.amount, selectedPayment.currencyIsoCode);
  }, [selectedPaymentOptionId, payments]);

  const disabled = useMemo(
    () => !isChecked || !selectedPaymentMethod || !selectedPaymentOptionId,
    [isChecked, selectedPaymentMethod, selectedPaymentOptionId],
  );

  if (!data) return null;
  return (
    <>
      <AnnouncementBar />
      <Navbar variant="secondary" />
      <ItineraryCheckoutHeader />
      <EnforcePageWidth>
        <Spacer size="lg" />
        {data.hasSplitPayments && (
          <>
            <Spacer size="lg" />
            <Paragraph size="lg">
              Since you have opted for a split invoice, we can no longer offer payment here. The invoice and
              corresponding payment link have been sent to you by e-mail.
            </Paragraph>
            <Spacer size="xl" />
          </>
        )}

        {!data.hasSplitPayments && (
          <StyledFlexRow gap={10}>
            <StyledFlexColumn flex="2" gap={4}>
              <StyledDesktopView>
                <Tag>Step 3 of 3</Tag>
              </StyledDesktopView>
              <StyledMobileView margin="0 auto">
                <Tag>Step 3 of 3</Tag>
                <Spacer size="md" />
              </StyledMobileView>
              <StyledMobileView>
                <OverviewCard secondary />
              </StyledMobileView>
              <StyledDesktopView>
                <h2>Payment overview</h2>
                <Paragraph color="tertiary">
                  Please note that the payment is split into two parts: a down payment and a residual payment. The down
                  payment is 25% of the total amount and must be paid at the time of booking. The residual payment is
                  75% of the total amount and must be paid no later than 60 days before departure.
                </Paragraph>
              </StyledDesktopView>

              <Spacer size="md" />

              <StyledFlexColumn gap={2}>
                <h4>Payment - how does it work?</h4>
                <Spacer size="sm" />
                <Paragraph weight={700}>1. Down payment</Paragraph>
                <Paragraph>25% of the total amount - to be paid at time of booking</Paragraph>
                <Paragraph weight={700}>2. Residual payment</Paragraph>
                <Paragraph>75% of the total amount - payable no later than 60 days before departure</Paragraph>
                <Spacer size="md" />

                <Divider />

                {!formattedPrice && (
                  <>
                    <Spacer size="md" />
                    <h4>Payments</h4>
                    {payments.length === 0 && <Paragraph>No payments have yet been made for this trip.</Paragraph>}

                    {payments.length > 0 && (
                      <StyledTableContainer>
                        <StyledTable>
                          <thead>
                            <tr>
                              <StyledTableHeader>Type</StyledTableHeader>
                              <StyledTableHeader>Amount</StyledTableHeader>
                              <StyledTableHeader>Status</StyledTableHeader>
                              <StyledTableHeader> </StyledTableHeader>
                            </tr>
                          </thead>
                          <tbody>
                            {payments.map((payment, index) => (
                              <StyledTableRow key={payment.id} $isLast={index === payments.length - 1}>
                                <StyledTableCell>
                                  {payment.type === 'Down Payment' ? 'Down payment' : 'Residual payment'}
                                </StyledTableCell>
                                <StyledTableCell>
                                  {formatPrice(payment.amount, payment.currencyIsoCode)}
                                </StyledTableCell>
                                <StyledTableCell>
                                  <StyledPaymentStatus $status={payment.status}>{payment.status}</StyledPaymentStatus>
                                </StyledTableCell>
                                <StyledTableCell $align="center">
                                  {payment.status === 'Open' && (
                                    <Button onClick={() => setSelectedPaymentOptionId(payment.id)} size="extraSmall">
                                      Pay
                                    </Button>
                                  )}
                                </StyledTableCell>
                              </StyledTableRow>
                            ))}
                          </tbody>
                        </StyledTable>
                      </StyledTableContainer>
                    )}
                  </>
                )}

                {formattedPrice && (
                  <>
                    <Spacer size="sm" />
                    <Checkbox checked={isChecked} onChange={handleCheckboxChange}>
                      I agree to the&nbsp;
                      <StyledLink href="https://www.google.com" target="_blank">
                        terms and conditions
                      </StyledLink>
                    </Checkbox>

                    <Spacer size="md" />
                    <h4>Pay now | {formattedPrice}</h4>
                    <StyledMobileView>
                      <Paragraph color="tertiary">
                        Select a payment method to pay the remaining amount of {formattedPrice}.
                      </Paragraph>
                    </StyledMobileView>

                    <StyledOption $opened={opened === 'ideal'} onClick={() => toggleOpened('ideal')}>
                      <StyledOptionHeader>
                        <StyledLogo src="https://upload.wikimedia.org/wikipedia/commons/thumb/e/e9/IDEAL_Logo.png/1200px-IDEAL_Logo.png" />
                        IDeal
                      </StyledOptionHeader>
                      <StyledOptionContent $opened={opened === 'ideal'}>
                        <Spacer size="none" />
                        <Divider />
                        <Button
                          variant="secondary"
                          size="small"
                          fullWidth
                          disabled={disabled}
                          onClick={handlePayment}
                          disableCapslock
                        >
                          Pay
                        </Button>
                      </StyledOptionContent>
                    </StyledOption>
                    <StyledOption $opened={opened === 'creditcard'} onClick={() => toggleOpened('creditcard')}>
                      <StyledOptionHeader>
                        <StyledLogo src="https://static-00.iconduck.com/assets.00/visa-icon-1024x656-8qb47rxg.png" />
                        <StyledLogo src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" />
                        Credit Card
                      </StyledOptionHeader>
                      <StyledOptionContent $opened={opened === 'creditcard'}>
                        <Spacer size="none" />
                        <Divider />
                        <Button
                          variant="secondary"
                          size="small"
                          fullWidth
                          disabled={disabled}
                          onClick={handlePayment}
                          disableCapslock
                        >
                          Pay
                        </Button>
                      </StyledOptionContent>
                    </StyledOption>

                    <Spacer size="md" />
                    <Button onClick={() => setSelectedPaymentOptionId(null)} size="extraSmall">
                      Back
                    </Button>
                  </>
                )}
              </StyledFlexColumn>
            </StyledFlexColumn>

            <StyledDesktopView flex="1">
              <OverviewCard secondary />
            </StyledDesktopView>
          </StyledFlexRow>
        )}
        <Spacer size="2xl" />
      </EnforcePageWidth>
    </>
  );
}
